// CSV exports of the schedule as members will receive it.
//
// Both files are built from buildPlayerSchedules()/buildRecipients() — the
// same functions that render the emails — so the export is a faithful preview
// of the mail-out rather than a second, drifting view of the data.
//
// These files contain member names and email addresses. They are downloaded
// to the operator's machine and are never written into the repo.

import { buildPlayerSchedules, buildRecipients } from "./scheduleEmails";
import { addHours } from "./time";

const csvCell = value => {
  const str = value == null ? "" : String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const toCsv = (headers, rows) =>
  [headers, ...rows].map(row => row.map(csvCell).join(",")).join("\r\n");

const sessionType = session =>
  session.kind === "private" ? "Private lesson"
  : session.kind === "openplay" ? "Open play"
  : "Group clinic";

/**
 * One row per player per session. A player with no session still gets a row
 * (blank schedule columns) so nobody silently disappears from the export, and
 * the "In mail-out" column shows whether they'd actually be emailed.
 */
export function membersCsv(players = [], groups = []) {
  const headers = [
    "Member", "Email", "Classification", "Programme", "Group ID", "Session type",
    "Day", "Start", "End", "Hours", "Court", "Group size", "Others in group", "In mail-out",
  ];

  const rows = [];
  buildPlayerSchedules(players, groups).forEach(person => {
    const reachable = person.email ? "Yes" : "No — no email address";
    if (person.sessions.length === 0) {
      rows.push([
        person.name, person.email, person.classification, "", "", "No session",
        "", "", "", "", "", "", "",
        person.email ? "No — not placed in a group" : "No — no email address",
      ]);
      return;
    }
    person.sessions.forEach(s => {
      rows.push([
        person.name, person.email, person.classification, s.tier, s.id, sessionType(s),
        s.day, s.startTime, s.startTime ? addHours(s.startTime, s.duration) : "", s.duration,
        s.court, s.playerIds.length, s.groupmates.join(", "), reachable,
      ]);
    });
  });

  return toCsv(headers, rows);
}

/**
 * One row per email address — the actual mail-out list, matching how the Email
 * tab groups a shared parent address into a single message.
 */
export function recipientsCsv(players = [], groups = []) {
  const headers = ["Email", "People at this address", "People", "Sessions", "Will be emailed", "Schedule summary"];

  const rows = buildRecipients(players, groups).map(r => [
    r.email,
    r.people.length,
    r.people.map(p => p.name).join(", "),
    r.sessionCount,
    r.sessionCount > 0 ? "Yes" : "No — no session to send",
    r.people
      .map(p =>
        p.sessions.length
          ? `${p.name}: ${p.sessions.map(s => `${s.day} ${s.time}${s.court ? ` (${s.court})` : ""}`).join("; ")}`
          : `${p.name}: not placed`
      )
      .join(" | "),
  ]);

  return toCsv(headers, rows);
}

/** Triggers a browser download. Excel opens UTF-8 CSV correctly only with a BOM. */
export function downloadCsv(filename, csv) {
  const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** "kac-member-schedules-fall-2026.csv" */
export const exportFilename = (base, term) => {
  const slug = String(term || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `kac-${base}${slug ? `-${slug}` : ""}.csv`;
};
